/**
 * EGAp Iteration Store
 *
 * Manages EGAp Iterations - within-level execution containers for DEGAP cycles.
 * Per the parent-child model: Catchball Loops → EGAp Iterations → Sessions
 *
 * An EGAp Iteration represents one pass of E→G→A work to produce a proposal.
 *
 * FROZEN (ADR-catchball-consolidation / Phase B4): do not route new Catchball
 * authority or ball-position logic through EGAp. Ball tree + level turns are
 * the authority path; proposal packets (Layer 3) stay unchanged. Leave this
 * store for legacy within-level DEGAP work only — no new Catchball callers.
 */

import { create } from 'zustand';
import { v4 as uuid } from 'uuid';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import {
  type EGApIteration,
  type EGApPhase,
  type EGApScopeLevel,
  type EGApIterationStatus,
  type EGApGateResult,
  type EGApGateStatus,
  type CreateEGApIterationInput,
  type UpdateEGApIterationInput,
} from '../../types/prioritization/egapIteration';

// ============================================================================
// Types
// ============================================================================

interface EGApIterationStore {
  // State
  iterations: EGApIteration[];
  isLoading: boolean;
  error: string | null;
  
  // CRUD Actions
  createIteration: (input: CreateEGApIterationInput, createdBy?: string) => Promise<EGApIteration>;
  updateIteration: (iterationId: string, updates: UpdateEGApIterationInput) => Promise<void>;
  deleteIteration: (iterationId: string) => Promise<void>;
  
  // Phase Advancement
  advancePhase: (iterationId: string, targetPhase: EGApPhase) => Promise<EGApGateResult>;
  completeCurrentPhase: (iterationId: string) => Promise<boolean>;
  
  // Gate Operations
  lockCriteria: (iterationId: string) => Promise<void>;
  markValuesConverged: (iterationId: string) => Promise<void>;
  lockCandidatePool: (iterationId: string, focusListId: string) => Promise<void>;
  unlockCandidatePool: (iterationId: string) => Promise<void>;
  
  // Status Operations
  markReadyToToss: (iterationId: string) => Promise<void>;
  markTossed: (iterationId: string) => Promise<void>;
  markAccepted: (iterationId: string) => Promise<void>;
  markSuperseded: (iterationId: string) => Promise<void>;
  
  // Selectors
  getIteration: (iterationId: string) => EGApIteration | undefined;
  getIterationsForCycle: (cycleId: string) => EGApIteration[];
  getIterationsForLoop: (catchballLoopId: string) => EGApIteration[];
  getActiveIteration: (cycleId: string) => EGApIteration | undefined;
  getActiveIterationForScope: (cycleId: string, scopeLevel: EGApScopeLevel, scopeEntityId?: string) => EGApIteration | undefined;
  getGateStatus: (iterationId: string) => EGApGateStatus | null;
  
  // Summary Updates
  updateIterationSummary: (iterationId: string) => Promise<void>;
  
  // Sync
  fetchFromSupabase: (cycleId?: string) => Promise<void>;
  
  // Utilities
  clearError: () => void;
}

// ============================================================================
// Helper: Sync iteration to Supabase
// ============================================================================

async function syncIterationToSupabase(iteration: EGApIteration): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  
  try {
    const { error } = await supabase
      .from('egap_iterations')
      .upsert({
        id: iteration.id,
        cycle_id: iteration.cycleId,
        catchball_loop_id: iteration.catchballLoopId,
        scope_level: iteration.scopeLevel,
        scope_entity_id: iteration.scopeEntityId,
        iteration_index: iteration.iterationIndex,
        phase: iteration.phase,
        engage_completed_at: iteration.engageCompletedAt?.toISOString(),
        gather_completed_at: iteration.gatherCompletedAt?.toISOString(),
        arrange_completed_at: iteration.arrangeCompletedAt?.toISOString(),
        prioritize_completed_at: iteration.prioritizeCompletedAt?.toISOString(),
        criteria_locked_at: iteration.criteriaLockedAt?.toISOString(),
        values_converged_at: iteration.valuesConvergedAt?.toISOString(),
        candidate_pool_locked_at: iteration.candidatePoolLockedAt?.toISOString(),
        status: iteration.status,
        proposal_focus_list_id: iteration.proposalFocusListId,
        request_context: iteration.requestContext,
        iteration_summary: iteration.iterationSummary,
        created_by: iteration.createdBy,
        created_at: iteration.createdAt.toISOString(),
        updated_at: iteration.updatedAt.toISOString(),
        workspace_id: iteration.workspaceId,
        tenant_id: iteration.tenantId,
      });
    
    if (error) {
      console.error('[EGApIterationStore] Failed to sync iteration:', error);
    }
  } catch (err) {
    console.error('[EGApIterationStore] Sync error:', err);
  }
}

// ============================================================================
// Helper: Convert DB row to iteration
// ============================================================================

function rowToIteration(row: Record<string, unknown>): EGApIteration {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    catchballLoopId: row.catchball_loop_id as string | undefined,
    scopeLevel: row.scope_level as EGApScopeLevel,
    scopeEntityId: row.scope_entity_id as string | undefined,
    iterationIndex: row.iteration_index as number,
    phase: row.phase as EGApPhase,
    engageCompletedAt: row.engage_completed_at ? new Date(row.engage_completed_at as string) : undefined,
    gatherCompletedAt: row.gather_completed_at ? new Date(row.gather_completed_at as string) : undefined,
    arrangeCompletedAt: row.arrange_completed_at ? new Date(row.arrange_completed_at as string) : undefined,
    prioritizeCompletedAt: row.prioritize_completed_at ? new Date(row.prioritize_completed_at as string) : undefined,
    criteriaLockedAt: row.criteria_locked_at ? new Date(row.criteria_locked_at as string) : undefined,
    valuesConvergedAt: row.values_converged_at ? new Date(row.values_converged_at as string) : undefined,
    candidatePoolLockedAt: row.candidate_pool_locked_at ? new Date(row.candidate_pool_locked_at as string) : undefined,
    status: row.status as EGApIterationStatus,
    proposalFocusListId: row.proposal_focus_list_id as string | undefined,
    requestContext: row.request_context as EGApIteration['requestContext'],
    iterationSummary: row.iteration_summary as EGApIteration['iterationSummary'],
    createdBy: row.created_by as string | undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
    workspaceId: row.workspace_id as string | undefined,
    tenantId: row.tenant_id as string | undefined,
  };
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useEGApIterationStore = create<EGApIterationStore>((set, get) => ({
  // Initial state
  iterations: [],
  isLoading: false,
  error: null,
  
  // -------------------------------------------------------------------------
  // CRUD: Create
  // -------------------------------------------------------------------------
  createIteration: async (input, createdBy) => {
    // Calculate iteration index for this scope
    const existingIterations = get().iterations.filter(
      i => i.cycleId === input.cycleId 
        && i.catchballLoopId === input.catchballLoopId
        && i.scopeLevel === (input.scopeLevel || 'enterprise')
        && i.scopeEntityId === input.scopeEntityId
    );
    const nextIndex = existingIterations.length > 0 
      ? Math.max(...existingIterations.map(i => i.iterationIndex)) + 1 
      : 1;
    
    const now = new Date();
    const iteration: EGApIteration = {
      id: uuid(),
      cycleId: input.cycleId,
      catchballLoopId: input.catchballLoopId,
      scopeLevel: input.scopeLevel || 'enterprise',
      scopeEntityId: input.scopeEntityId,
      iterationIndex: nextIndex,
      phase: 'E',
      status: 'in_progress',
      requestContext: input.requestContext,
      iterationSummary: {
        criteriaCount: 0,
        itemCount: 0,
        sessionCount: 0,
        participantCount: 0,
      },
      createdBy,
      createdAt: now,
      updatedAt: now,
      workspaceId: input.workspaceId,
      tenantId: input.tenantId,
    };
    
    set(state => ({ iterations: [...state.iterations, iteration] }));
    
    // Sync to Supabase
    await syncIterationToSupabase(iteration);
    
    return iteration;
  },
  
  // -------------------------------------------------------------------------
  // CRUD: Update
  // -------------------------------------------------------------------------
  updateIteration: async (iterationId, updates) => {
    set(state => ({
      iterations: state.iterations.map(i => {
        if (i.id !== iterationId) return i;
        return {
          ...i,
          ...updates,
          updatedAt: new Date(),
        };
      }),
    }));
    
    const iteration = get().getIteration(iterationId);
    if (iteration) {
      await syncIterationToSupabase(iteration);
    }
  },
  
  // -------------------------------------------------------------------------
  // CRUD: Delete
  // -------------------------------------------------------------------------
  deleteIteration: async (iterationId) => {
    const supabase = getSafeSupabaseClient();
    
    set(state => ({
      iterations: state.iterations.filter(i => i.id !== iterationId),
    }));
    
    if (supabase) {
      await supabase.from('egap_iterations').delete().eq('id', iterationId);
    }
  },
  
  // -------------------------------------------------------------------------
  // Phase Advancement
  // -------------------------------------------------------------------------
  advancePhase: async (iterationId, targetPhase) => {
    const iteration = get().getIteration(iterationId);
    
    if (!iteration) {
      return {
        canAdvance: false,
        currentPhase: 'E',
        targetPhase,
        satisfiedGates: [],
        blockers: ['Iteration not found'],
      };
    }
    
    const phaseOrder: EGApPhase[] = ['E', 'G', 'A', 'P'];
    const currentIndex = phaseOrder.indexOf(iteration.phase);
    const targetIndex = phaseOrder.indexOf(targetPhase);
    
    // Can only advance forward by one phase
    if (targetIndex !== currentIndex + 1) {
      return {
        canAdvance: false,
        currentPhase: iteration.phase,
        targetPhase,
        satisfiedGates: [],
        blockers: [`Cannot advance from ${iteration.phase} to ${targetPhase}. Must advance sequentially.`],
      };
    }
    
    // Check gates for current phase
    const blockers: string[] = [];
    const satisfiedGates: string[] = [];
    
    switch (iteration.phase) {
      case 'E':
        // E→G: need engage completed
        if (iteration.engageCompletedAt) {
          satisfiedGates.push('engage_completed');
        } else {
          blockers.push('Engage phase not marked complete');
        }
        break;
        
      case 'G':
        // G→A: need criteria locked
        if (iteration.criteriaLockedAt) {
          satisfiedGates.push('criteria_locked');
        } else {
          blockers.push('Criteria must be locked before ARRANGE');
        }
        break;
        
      case 'A':
        // A→P: need candidate pool locked
        if (iteration.candidatePoolLockedAt) {
          satisfiedGates.push('candidate_pool_locked');
        } else {
          blockers.push('Candidate pool must be locked before PRIORITIZE');
        }
        break;
    }
    
    const result: EGApGateResult = {
      canAdvance: blockers.length === 0,
      currentPhase: iteration.phase,
      targetPhase,
      satisfiedGates,
      blockers,
      remediationActions: blockers.map(b => ({
        gate: b,
        action: `Complete: ${b}`,
      })),
    };
    
    if (result.canAdvance) {
      // Mark current phase as completed and advance
      const completionField = `${iteration.phase.toLowerCase()}CompletedAt` as keyof EGApIteration;
      const updates: Partial<EGApIteration> = {
        [completionField]: new Date(),
        phase: targetPhase,
        updatedAt: new Date(),
      };
      
      await get().updateIteration(iterationId, updates as UpdateEGApIterationInput);
    }
    
    return result;
  },
  
  completeCurrentPhase: async (iterationId) => {
    const iteration = get().getIteration(iterationId);
    if (!iteration) return false;
    
    const completionField = `${iteration.phase.toLowerCase()}CompletedAt` as keyof EGApIteration;
    
    await get().updateIteration(iterationId, {
      [completionField]: new Date(),
    } as UpdateEGApIterationInput);
    
    return true;
  },
  
  // -------------------------------------------------------------------------
  // Gate Operations
  // -------------------------------------------------------------------------
  lockCriteria: async (iterationId) => {
    await get().updateIteration(iterationId, {
      criteriaLockedAt: new Date(),
    });
  },
  
  markValuesConverged: async (iterationId) => {
    await get().updateIteration(iterationId, {
      valuesConvergedAt: new Date(),
    });
  },
  
  lockCandidatePool: async (iterationId, focusListId) => {
    await get().updateIteration(iterationId, {
      candidatePoolLockedAt: new Date(),
      proposalFocusListId: focusListId,
    });
  },

  unlockCandidatePool: async (iterationId) => {
    await get().updateIteration(iterationId, {
      candidatePoolLockedAt: undefined,
      proposalFocusListId: undefined,
    });
  },
  
  // -------------------------------------------------------------------------
  // Status Operations
  // -------------------------------------------------------------------------
  markReadyToToss: async (iterationId) => {
    const iteration = get().getIteration(iterationId);
    if (!iteration) return;
    
    // Validate that iteration has required outputs
    if (!iteration.proposalFocusListId) {
      set({ error: 'Cannot mark ready to toss: no proposal focus list set' });
      return;
    }
    
    await get().updateIteration(iterationId, {
      status: 'ready_to_toss',
    });
  },
  
  markTossed: async (iterationId) => {
    await get().updateIteration(iterationId, {
      status: 'tossed',
    });
  },
  
  markAccepted: async (iterationId) => {
    await get().updateIteration(iterationId, {
      status: 'accepted',
    });
  },
  
  markSuperseded: async (iterationId) => {
    await get().updateIteration(iterationId, {
      status: 'superseded',
    });
  },
  
  // -------------------------------------------------------------------------
  // Selectors
  // -------------------------------------------------------------------------
  getIteration: (iterationId) => {
    return get().iterations.find(i => i.id === iterationId);
  },
  
  getIterationsForCycle: (cycleId) => {
    return get().iterations
      .filter(i => i.cycleId === cycleId)
      .sort((a, b) => a.iterationIndex - b.iterationIndex);
  },
  
  getIterationsForLoop: (catchballLoopId) => {
    return get().iterations
      .filter(i => i.catchballLoopId === catchballLoopId)
      .sort((a, b) => a.iterationIndex - b.iterationIndex);
  },
  
  getActiveIteration: (cycleId) => {
    return get().iterations.find(
      i => i.cycleId === cycleId && i.status === 'in_progress'
    );
  },
  
  getActiveIterationForScope: (cycleId, scopeLevel, scopeEntityId) => {
    return get().iterations.find(
      i => i.cycleId === cycleId 
        && i.scopeLevel === scopeLevel
        && i.scopeEntityId === scopeEntityId
        && i.status === 'in_progress'
    );
  },
  
  getGateStatus: (iterationId) => {
    const iteration = get().getIteration(iterationId);
    if (!iteration) return null;
    
    return {
      // E→G gates
      engageComplete: iteration.engageCompletedAt != null,
      participantsActive: true, // Would need to query participants
      
      // G→A gates
      criteriaSessionComplete: false, // Would need to query sessions
      criteriaLocked: iteration.criteriaLockedAt != null,
      valuesConverged: iteration.valuesConvergedAt != null,
      
      // A→P gates
      candidatePoolSessionComplete: false, // Would need to query sessions
      candidatePoolLocked: iteration.candidatePoolLockedAt != null,
      
      // P completion gates
      rankingSessionComplete: false, // Would need to query sessions
      proposalReady: iteration.proposalFocusListId != null,
    };
  },
  
  // -------------------------------------------------------------------------
  // Summary Updates
  // -------------------------------------------------------------------------
  updateIterationSummary: async (iterationId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    
    const iteration = get().getIteration(iterationId);
    if (!iteration) return;
    
    // Query counts from database
    const [sessionsResult, participantsResult] = await Promise.all([
      supabase
        .from('prioritization_sessions')
        .select('id', { count: 'exact' })
        .eq('egap_iteration_id', iterationId),
      supabase
        .from('cycle_participants')
        .select('id', { count: 'exact' })
        .eq('cycle_id', iteration.cycleId)
        .eq('status', 'active'),
    ]);
    
    const summary: EGApIteration['iterationSummary'] = {
      sessionCount: sessionsResult.count || 0,
      participantCount: participantsResult.count || 0,
      criteriaCount: 0, // Would need to query comparison sets
      itemCount: 0, // Would need to query focus lists
    };
    
    await get().updateIteration(iterationId, {
      iterationSummary: summary,
    });
  },
  
  // -------------------------------------------------------------------------
  // Sync from Supabase
  // -------------------------------------------------------------------------
  fetchFromSupabase: async (cycleId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    
    set({ isLoading: true, error: null });
    
    try {
      let query = supabase.from('egap_iterations').select('*');
      
      if (cycleId) {
        query = query.eq('cycle_id', cycleId);
      }
      
      const { data, error } = await query.order('created_at', { ascending: true });
      
      if (error) {
        console.error('[EGApIterationStore] Fetch error:', error);
        set({ error: error.message, isLoading: false });
        return;
      }
      
      if (data) {
        const iterations = data.map(rowToIteration);
        
        if (cycleId) {
          // Merge with existing iterations from other cycles
          set(state => ({
            iterations: [
              ...state.iterations.filter(i => i.cycleId !== cycleId),
              ...iterations,
            ],
            isLoading: false,
          }));
        } else {
          set({ iterations, isLoading: false });
        }
      }
    } catch (err) {
      console.error('[EGApIterationStore] Fetch error:', err);
      set({ error: 'Failed to fetch iterations', isLoading: false });
    }
  },
  
  // -------------------------------------------------------------------------
  // Utilities
  // -------------------------------------------------------------------------
  clearError: () => set({ error: null }),
}));

/**
 * Neutral alias for the legacy EGAp iteration store hook.
 * Keep the underlying file/schema names intact while callers migrate to non-legacy terminology.
 */
export const useExecutionLineageStore = useEGApIterationStore;
